import React, {FC} from 'react';

import {sendDesktopAnalyticsPerSession} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Icon} from '@optimaxdev/icons';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';
import {useStripe} from 'hooks/useStripe/useStripe';
import {PaymentType} from 'types/PaymentMethodsType';

import {PAYMENTS} from '../../constants/paymentStep';
import {useGetAddressForStripe} from '../../hooks/useGetBillingDetailsForStripe/useGetBillingDetailsForStripe';
import * as CardsIcon from '../../img/cards.svg';
import {setOptionPaymentMethod as setOptionPaymentMethodAction} from '../../store/reducers/analytics/analytics';
import {sendPayerInfoToSaga} from '../../store/reducers/paymentStep/payerInfo/payerInfo';
import {
    getIsSelectedByKey,
    getPaymentMethodByKey,
    getStatusOfLoadingSaveOrder,
} from '../../store/selectors/paymentStep/paymentStep';
import {PaymentFooter} from '../paymentFooter/paymentFooter';
import {PaymentWrapperConnected} from '../paymentWrapper/paymentWrapper';

import {settings} from './constant';

import s from './paymentStripe.scss';

export type PropsType = {
    method: PaymentType;
    isSelected: boolean;
    runSavePayment: (data: {paymentId: string; method: string}) => void;
    setOptionPaymentMethod: () => void;
    isSaveLoading: boolean;
};

/**
 * Payment Stripe for checkout
 *
 * @constructor
 */
export const PaymentStripe: FC<PropsType> = ({
    isSaveLoading,
    isSelected,
    method: {code, publishableKey},
    runSavePayment,
    setOptionPaymentMethod,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {createToken, error, isCreateTokenLoading} = useStripe(String(publishableKey), {
        settings,
    });
    const billingDetails = useGetAddressForStripe();
    const isLoading = isSaveLoading || isCreateTokenLoading;

    const handleOnSubmit = () => {
        createToken(billingDetails, {
            onSuccess: ({id}) => {
                runSavePayment({method: code, paymentId: id});
                setOptionPaymentMethod();
            },
        });
    };

    const sendAnalytics = () => {
        sendDesktopAnalyticsPerSession({
            event: CHECKOUT_PAYMENT_STEP,
            label: {
                message: 'Credit Card - Card image clicked',
                method: 'Payment',
            },
        });
    };

    return (
        <PaymentWrapperConnected
            rightSideIcon={
                <Icon
                    className={s.cards}
                    height={23}
                    icon={CardsIcon}
                    label="CardsIcon"
                    onClick={sendAnalytics}
                    width={245}
                />
            }
            title={
                <div className={s.title}>
                    <span>Credit card</span>
                </div>
            }
            code={code}
            isSelected={isSelected}
        >
            <form name="credit card form">
                <div className={classNames(s.wrap, {[s.wrapFacelift]: isFacelift})}>
                    <div className={s.card} />
                    <div id="cardNumberStripe" />
                    <div id="expDateStripe" />
                    <div id="cvvStripe" />
                </div>
                {error && <p className={s.error}>{error}</p>}
                <PaymentFooter isLoading={isLoading} onSubmit={handleOnSubmit} />
            </form>
        </PaymentWrapperConnected>
    );
};
/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    isSaveLoading: getStatusOfLoadingSaveOrder(store),
    isSelected: getIsSelectedByKey(store, PAYMENTS.METHOD_STRIPE),
    method: getPaymentMethodByKey(store, PAYMENTS.METHOD_STRIPE),
});

const mapDispatchToProps = {
    runSavePayment: sendPayerInfoToSaga,
    setOptionPaymentMethod: setOptionPaymentMethodAction,
};

export const PaymentStripeConnected = connect(mapStateToProps, mapDispatchToProps)(PaymentStripe);
